import React from 'react';
import type { SsoPollResponse, SsoProvider, SsoStartResponse } from '../../../shared/types';
import type { Api } from './api';

export const SSO_LABEL: Record<SsoProvider, string> = { google: 'Google', microsoft: 'Microsoft' };

const isWeb = () => /^https?:$/.test(location.protocol);

/**
 * Accesso (o collegamento) con Google/Microsoft: apre la scelta dell'account nel browser
 * e aspetta che l'utente finisca. L'app ritira il risultato col suo codice segreto.
 * Va chiamata direttamente dal clic (sul web la scheda si apre subito, altrimenti il browser la blocca).
 */
export async function runSso(api: Api, path: string, body: Record<string, unknown>, cancelled: () => boolean): Promise<SsoPollResponse> {
  // Web: scheda aperta subito (vuota) e poi indirizzata. App Windows: si apre il browser di sistema.
  const tab = isWeb() ? window.open('', '_blank') : null;
  let start: SsoStartResponse;
  try {
    start = await api.post<SsoStartResponse>(path, body);
  } catch (e) {
    tab?.close();
    throw e;
  }
  if (tab) tab.location.href = start.url;
  else window.open(start.url, '_blank');

  const until = Date.now() + 10 * 60_000;
  while (Date.now() < until) {
    await new Promise((r) => setTimeout(r, 1500));
    if (cancelled()) return { status: 'error', message: '' };
    const r = await api.post<SsoPollResponse>('/api/auth/sso/poll', { pollKey: start.pollKey }).catch((e: Error) => ({ status: 'error' as const, message: e.message }));
    if (r.status !== 'pending') return r;
  }
  return { status: 'error', message: 'Tempo scaduto: riprova.' };
}

/** Loghi ufficiali (colori del marchio) per i pulsanti. */
export function SsoLogo({ provider, size = 18 }: { provider: SsoProvider; size?: number }) {
  if (provider === 'google') {
    return (
      <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
